import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useUnmount } from 'react-use';

import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { isCaptionVideoTask } from '~/components/CaptionVideoTasks/isCaptionVideoTask';
import { useEditModeStatus } from '~/components/EditMode/hooks/useEditModeStatus';
import { isVideoClipTask } from '~/components/VideoClipTasks/isVideoClipTask';
import { useSocketConnectionChange } from '~/providers/SocketContext/hooks/useSocketConnectionChange';
import { centralizedClipIdSelector, centralizedClipWorkspaceIdSelector } from '~/store/centralizedClip/selectors';
import { makeAssetTransformationTasksSelectorByWorkspaceId } from '~/store/tasks/selectors';
import { TRANSFORM_TYPE as CLIP_VIDEO_TRANSFORM_TYPE } from '~/swr-hooks/clips/useCreateVideoClipTask';
import { ReadyState } from '~/types/sockets';
import { useAirStore } from '~/utils/ReduxUtils';
import { useCancelTask } from '~/utils/taskUtils/useCancelTask';
import { usePrivateSyncTasks } from '~/utils/taskUtils/usePrivateSyncTasks';

export const useAssetTransformationTaskEvent = () => {
  const store = useAirStore();
  const workspaceId = useSelector(centralizedClipWorkspaceIdSelector);
  const assetVersionId = useSelector(centralizedClipIdSelector);
  const tasksSelector = useMemo(() => makeAssetTransformationTasksSelectorByWorkspaceId(workspaceId), [workspaceId]);
  const { setStatusIdle } = useEditModeStatus();
  const { cancelTask } = useCancelTask();
  const { showToast } = useToasts();

  const { syncLocalTasks } = usePrivateSyncTasks({
    workspaceId,
    tasksSelector,
    localType: 'AssetTransformation',
    remoteType: 'AssetTransformer',
    onError({ task, localTask }) {
      if (isCaptionVideoTask(localTask) || isVideoClipTask(localTask)) {
        return;
      }

      setStatusIdle();
      const errorMessage = task.data?.log?.at(-1) ?? 'There was an error transforming the image. Please try again.';
      showToast(errorMessage, { color: 'red' });
      reportErrorToBugsnag({
        error: new Error(`Unable to transform asset. Task ID: ${localTask.remoteTaskId}`),
        context: 'Unable to transform asset',
        metadata: {
          key: 'data',
          data: localTask.metadata,
        },
      });
    },
  });

  /**
   * Because sockets reconnect when the user's internet connection comes back online,
   * we don't need an explicit isOnline check but can piggyback off of the socket reconnecting
   */
  useSocketConnectionChange({
    onChange: (readyState) => {
      if (readyState === ReadyState.OPEN) {
        syncLocalTasks();
      }
    },
  });

  useUnmount(() => {
    const taskIds = tasksSelector(store.getState())
      .filter(
        ({ metadata }) =>
          'assetVersionId' in metadata &&
          metadata.assetVersionId === assetVersionId &&
          metadata.transformType !== CLIP_VIDEO_TRANSFORM_TYPE &&
          metadata.transformType !== 'captionVideo',
      )
      .map(({ localTaskId }) => localTaskId);

    if (taskIds.length > 0) {
      taskIds.forEach((taskId) => {
        cancelTask(taskId);
      });
    }
  });
};
